import { observable, action, makeObservable, runInAction } from 'mobx';
import ReactGA from 'react-ga4';
import * as Sentry from '@sentry/react';

import { auth, firebase, db as firestoreDb, fn, FieldValue } from 'firebase-modules';
import { createRouterState, RouterState } from 'mobx-state-router';
import { isValidEmail, normalizeEmail, isGuestAuthUid } from '@creative-kit/shared';
import type { RootStore } from '.';
import { RouteNames } from './routes';
import { db } from './data';
import { withStartupSpan, enableStartupTracing, finishStartupTracing } from './startupTracing';

interface AuthUser {
  uid: string;
  avatarUrl: string;
  displayName: string | null;
  email: string | null;
  canUseApp?: boolean;
  hasOrganization?: boolean;
  organizationId?: string;
  organizationRole?: string;
  isCQAdmin?: boolean;
  isGuest?: boolean;
  guestSessionId?: string;
  guestPermission?: 'VIEW' | 'EDIT' | 'OWNER';
  guestExpiresAt?: number;
  hasPendingInvitation?: boolean;
  pendingInvitationEmail?: string;
}

export class AuthStore {
  private rootStore: RootStore;
  user?: AuthUser | null;
  userLoading: boolean = true;
  signInRedirect: RouterState = createRouterState(RouteNames.PROJECTS);
  private guestExpirationCheckInterval: NodeJS.Timeout | null = null;
  /** First onAuthStateChanged only — later token refresh / re-auth must not emit startup.* spans. */
  private hasBootstrappedAuth = false;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      user: observable,
      userLoading: observable,
      updateUser: action.bound,
      authStateChanged: action.bound,
      sendEmailLink: action.bound,
      verifyEmailLink: action.bound,
      logout: action.bound,
      setSignInRedirect: action.bound,
    });

    this.rootStore = rootStore;
  }

  init() {
    auth.onAuthStateChanged(this.authStateChanged);
    // Check for email link authentication on app load
    this.verifyEmailLink();
    // Start periodic expiration check for guest users
    this.startGuestExpirationCheck();
  }

  private startGuestExpirationCheck() {
    // Check every minute if there's a guest user
    this.guestExpirationCheckInterval = setInterval(async () => {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        return;
      }

      try {
        const tokenResult = await firebaseUser.getIdTokenResult(false); // Don't force refresh to avoid rate limits
        if (tokenResult.claims.guest === true) {
          const expiresAt = tokenResult.claims.expiresAt as number;
          if (expiresAt && expiresAt < Date.now()) {
            // Guest access expired, clear cache and sign out
            // eslint-disable-next-line no-console
            console.warn(
              '[AuthStore] Guest access expired (periodic check), clearing cache and signing out',
            );
            await this.clearGuestCache();
            await auth.signOut();
          }
        }
      } catch (error) {
        // Silently fail - don't spam console
      }
    }, 60000); // Check every 60 seconds
  }

  /**
   * Clears all cached data for guest users to prevent access after expiration
   * This includes Firestore cache, localStorage, and sessionStorage
   */
  // eslint-disable-next-line class-methods-use-this
  private async clearGuestCache() {
    try {
      // Clear Firestore persistence cache
      const { db: firebaseDb } = await import('firebase-modules');
      try {
        // Clear Firestore cache by disabling/enabling network
        // Note: This requires Firestore to be initialized
        await firebaseDb.clearPersistence?.();
      } catch (persistenceError) {
        // clearPersistence can fail if there are active listeners or if persistence is disabled
        // This is expected in some cases, so we'll continue
        // eslint-disable-next-line no-console
        console.warn('[AuthStore] Could not clear Firestore persistence:', persistenceError);
      }

      // Clear guest-specific localStorage items
      // Keep theme preferences and other non-sensitive data
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && (key.includes('guest') || key.includes('session') || key.includes('firebase'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Ignore errors removing individual items
        }
      });

      // Clear sessionStorage completely for guest users
      try {
        sessionStorage.clear();
      } catch (e) {
        // Ignore errors clearing sessionStorage
      }

      // Clear IndexedDB if used by Firestore
      // Note: This is handled by clearPersistence above, but we can also try manually
      if ('indexedDB' in window) {
        try {
          const deleteReq = indexedDB.deleteDatabase('firebaseLocalStorageDb');
          deleteReq.onsuccess = () => {
            // eslint-disable-next-line no-console
            console.log('[AuthStore] Cleared IndexedDB cache');
          };
        } catch (e) {
          // Ignore IndexedDB errors
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[AuthStore] Error clearing guest cache:', error);
      // Continue with sign out even if cache clearing fails
    }
  }

  private stopGuestExpirationCheck() {
    if (this.guestExpirationCheckInterval) {
      clearInterval(this.guestExpirationCheckInterval);
      this.guestExpirationCheckInterval = null;
    }
  }

  /**
   * Ensures user document exists in Firestore and has correct data
   * Called when a user authenticates - creates doc if missing, updates name if needed
   */
  // eslint-disable-next-line class-methods-use-this
  async ensureUserDocument() {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;

    if (isGuestAuthUid(firebaseUser.uid)) {
      return;
    }

    try {
      const tokenResult = await firebaseUser.getIdTokenResult(false);
      if (tokenResult.claims.guest === true) {
        return;
      }
    } catch {
      // Continue — onUserCreation also skips guest users
    }

    try {
      const userDoc = await db.users(firebaseUser.uid).get();
      const userData = userDoc.data();
      const needsUpdate =
        userDoc.exists && (!userData?.name || userData?.name === null || userData?.name === '');
      // Re-sync when user doc claims org membership but organizationId is missing (partial claim/approval)
      const needsOrgSync =
        userDoc.exists && !userData?.organizationId && userData?.hasOrganization === true;

      if (!userDoc.exists || needsUpdate || needsOrgSync) {
        // User document doesn't exist OR name is missing/null - create/update via Cloud Function
        // onUserCreation will handle both creating new docs and updating existing ones
        const createUserFn = fn.httpsCallable('onUserCreation');
        await createUserFn();
        // eslint-disable-next-line no-console
        console.log(`[AuthStore] Called onUserCreation for ${firebaseUser.uid}`, {
          docExists: userDoc.exists,
          needsUpdate,
          currentName: userData?.name,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[AuthStore] Failed to ensure user document:', error);
      // Non-critical error, continue
    }
  }

  async updateUser() {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      runInAction(() => {
        this.user = null;
      });
      Sentry.setUser(null);
      ReactGA.set({
        userId: null,
        user_properties: null,
      });
    } else {
      const userId = firebaseUser.uid;

      // Get custom claims to check for guest access
      let isGuest = false;
      let guestSessionId: string | undefined;
      let guestPermission: 'VIEW' | 'EDIT' | 'OWNER' | undefined;
      let guestExpiresAt: number | undefined;

      try {
        const tokenResult = await withStartupSpan('startup.auth.token', 'Refresh auth token', () =>
          firebaseUser.getIdTokenResult(true),
        );

        // Check if user has guest claims
        const hasGuestClaims = tokenResult.claims.guest === true;

        if (hasGuestClaims) {
          isGuest = true;
          guestSessionId = tokenResult.claims.sessionId as string;
          guestPermission = tokenResult.claims.permission as 'VIEW' | 'EDIT' | 'OWNER';
          guestExpiresAt = tokenResult.claims.expiresAt as number;

          // Check if guest access has expired
          if (guestExpiresAt && guestExpiresAt < Date.now()) {
            // Guest access expired, clear cache and sign out
            // eslint-disable-next-line no-console
            console.warn('[AuthStore] Guest access expired, clearing cache and signing out');
            await this.clearGuestCache();
            await auth.signOut();
            return;
          }
        } else {
          // User previously had guest claims but they've been revoked (claims set to null)
          // Check if user was previously a guest - if so, sign them out
          const wasGuest = this.user?.isGuest;
          if (wasGuest) {
            // eslint-disable-next-line no-console
            console.warn('[AuthStore] Guest claims revoked, signing out');
            await this.clearGuestCache();
            await auth.signOut();
            return;
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[AuthStore] Failed to get token result:', error);
        // If token retrieval fails, check if user was previously a guest
        // This handles cases where token refresh fails but user is still a guest
        if (this.user?.isGuest) {
          isGuest = true;
          guestSessionId = this.user.guestSessionId;
          guestPermission = this.user.guestPermission;
          guestExpiresAt = this.user.guestExpiresAt;
        }
      }

      runInAction(() => {
        this.user = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || firebaseUser.email || null,
          avatarUrl: firebaseUser.photoURL || '',
          isGuest,
          guestSessionId,
          guestPermission,
          guestExpiresAt,
        };
      });

      // For guest users, skip all organization/admin/invitation checks and return early
      // Guests use custom claims for access and don't need organization membership
      if (isGuest) {
        runInAction(() => {
          if (!this.user) return;
          this.user = {
            ...this.user,
            canUseApp: true, // Guests can use the app
            hasOrganization: false,
            organizationId: undefined,
            organizationRole: undefined,
            isCQAdmin: false,
            hasPendingInvitation: false,
            pendingInvitationEmail: undefined,
          };
        });
        return; // Early return to skip all organization/admin checks
      }

      Sentry.setUser({
        id: userId,
        email: firebaseUser.email || 'no@email.com',
        username: firebaseUser.displayName || 'No Name',
      });
      ReactGA.set({
        userId,
        user_properties: {
          email: firebaseUser.email || 'no@email.com',
          name: firebaseUser.displayName || 'No Name',
        },
      });

      // Skip user document loading for guest users - they don't have user documents
      let userDocOrganizationId: string | undefined;
      let userDocOrganizationRole: string | undefined;
      if (!isGuest) {
        try {
          const userDocData = await withStartupSpan(
            'startup.auth.user_document',
            'Load user document',
            async () => {
              // Always ensure user document exists and has correct data (especially name)
              await this.ensureUserDocument();
              const userDoc = await db.users(userId).get();
              return userDoc.data();
            },
          );
          userDocOrganizationId = userDocData?.organizationId;
          userDocOrganizationRole = userDocData?.organizationRole;
          runInAction(() => {
            if (!this.user) return;
            // IMPORTANT: Replace user object so MobX reactions (which observe `user` reference)
            // re-run reliably. Nested mutation on a plain object won't trigger reactions.
            this.user = { ...this.user, canUseApp: !!userDocData?.canUseApp };
          });
        } catch (userDocError: any) {
          // If permission denied, user might be a guest whose token retrieval failed
          // Check if error is permission-related and user ID suggests guest
          if (userDocError?.code === 'permission-denied' && userId.startsWith('guest_')) {
            isGuest = true;
            // Update user object to reflect guest status
            runInAction(() => {
              if (!this.user) return;
              this.user = {
                ...this.user,
                isGuest: true,
                canUseApp: true,
                hasOrganization: false,
                organizationId: undefined,
                organizationRole: undefined,
                isCQAdmin: false,
                hasPendingInvitation: false,
                pendingInvitationEmail: undefined,
              };
            });
            // Early return to skip organization checks
            return;
          }
          // eslint-disable-next-line no-console
          console.warn('[AuthStore] Failed to load user document:', userDocError);
          runInAction(() => {
            if (!this.user) return;
            this.user = { ...this.user, canUseApp: false };
          });
        }
      } else {
        // Guest users can use the app (they're authenticated via custom token)
        runInAction(() => {
          if (!this.user) return;
          this.user = { ...this.user, canUseApp: true };
        });
      }

      // Skip organization membership check for guest users
      // Guest users don't need organizations - they have direct session access via custom claims
      let hasOrganization = false;
      let organizationId: string | undefined;
      let organizationRole: string | undefined;

      if (!isGuest) {
        // Fast path: user doc already has organizationId (typical case). One member-doc read
        // instead of scanning every organization — the full scan was blocking app startup
        // for 10s+ on accounts with many orgs (CRE-281).
        await withStartupSpan(
          'startup.auth.org_membership',
          'Resolve organization membership',
          async (): Promise<string> => {
            let runSlowPath = !userDocOrganizationId;
            let pathTaken = userDocOrganizationId ? 'fast_pending' : 'slow';

            if (userDocOrganizationId) {
              try {
                const memberDoc = await firestoreDb
                  .collection('organizations')
                  .doc(userDocOrganizationId)
                  .collection('members')
                  .doc(userId)
                  .get();

                if (memberDoc.exists && memberDoc.data()?.status === 'active') {
                  hasOrganization = true;
                  organizationId = userDocOrganizationId;
                  organizationRole = memberDoc.data()?.role ?? userDocOrganizationRole;
                  runSlowPath = false;
                  pathTaken = 'fast';
                } else {
                  // Stale user-doc orgId (e.g. post-removeMember without user-doc cleanup, or re-invite
                  // to a new org while users/{uid} still points at the old one). Fall back to slow scan.
                  Sentry.captureMessage(
                    '[AuthStore] memberDoc absent or inactive for userDocOrganizationId',
                    {
                      level: Sentry.Severity.Info,
                      extra: {
                        userId,
                        organizationId: userDocOrganizationId,
                        reason: 'stale_user_doc_org_id',
                      },
                    },
                  );
                  hasOrganization = false;
                  organizationId = undefined;
                  organizationRole = undefined;
                  runSlowPath = true;
                  pathTaken = 'fast_stale_fallback_slow';
                }
              } catch (fallbackError) {
                // eslint-disable-next-line no-console
                console.warn(
                  '[AuthStore] Fast-path org membership lookup failed, falling back to org scan:',
                  fallbackError,
                );
                runSlowPath = true;
                pathTaken = 'fast_fallback_slow';
              }
            }

            if (runSlowPath) {
              // Slow path: no organizationId on user doc, transient fast-path error, or stale orgId
              // with missing/inactive member doc (re-invite after prior removal).
              try {
                const ORG_MEMBERSHIP_TIMEOUT_MS = 10000;
                const organizationsSnapshot = await Promise.race([
                  firestoreDb.collection('organizations').get(),
                  new Promise<never>((_, reject) => {
                    setTimeout(
                      () => reject(new Error('Organization membership check timed out')),
                      ORG_MEMBERSHIP_TIMEOUT_MS,
                    );
                  }),
                ]);

                const membershipChecks = organizationsSnapshot.docs.map(async (orgDoc) => {
                  try {
                    const memberDoc = await firestoreDb
                      .collection('organizations')
                      .doc(orgDoc.id)
                      .collection('members')
                      .doc(userId)
                      .get();

                    if (memberDoc.exists && memberDoc.data()?.status === 'active') {
                      return { orgId: orgDoc.id, role: memberDoc.data()?.role };
                    }
                    return null;
                  } catch (memberError) {
                    // eslint-disable-next-line no-console
                    console.warn(
                      '[AuthStore] Error checking membership in org',
                      orgDoc.id,
                      ':',
                      memberError,
                    );
                    return null;
                  }
                });

                const results = await Promise.all(membershipChecks);
                const activeMembership = results.find((membership) => membership !== null);
                if (activeMembership) {
                  hasOrganization = true;
                  organizationId = activeMembership.orgId;
                  organizationRole = activeMembership.role;
                }
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('[AuthStore] Error checking organization membership:', error);
              }
            }

            return pathTaken;
          },
          (pathTaken) => ({ path: pathTaken }),
        );

        // Stale users/{uid}.organizationId (legacy removeMember) forces slow org scan every login.
        // Self-heal when no active membership remains — do not restore login-time onUserCreation repair.
        if (!hasOrganization && userDocOrganizationId) {
          try {
            await withStartupSpan(
              'startup.auth.stale_org_cleanup',
              'Clear stale organization fields on user doc',
              async () => {
                await firestoreDb.collection('users').doc(userId).update({
                  organizationId: FieldValue.delete(),
                  hasOrganization: false,
                  organizationRole: FieldValue.delete(),
                });
              },
              { staleOrganizationId: userDocOrganizationId },
            );
          } catch (cleanupError) {
            // eslint-disable-next-line no-console
            console.warn('[AuthStore] Failed to clear stale organization fields:', cleanupError);
            Sentry.captureMessage('[AuthStore] stale org field cleanup failed', {
              level: Sentry.Severity.Warning,
              extra: {
                userId,
                staleOrganizationId: userDocOrganizationId,
              },
            });
          }
        }
      }

      // Check if user is CQ admin (skip for guest users)
      let isCQAdmin = false;
      if (!isGuest) {
        try {
          isCQAdmin = await withStartupSpan(
            'startup.auth.admin_check',
            'Check CQ admin status',
            async () => {
              const adminDoc = await firestoreDb.collection('cqAdmins').doc(userId).get();
              return adminDoc.exists && adminDoc.data()?.role !== undefined;
            },
          );
        } catch (adminError) {
          // eslint-disable-next-line no-console
          console.warn('[AuthStore] Error checking CQ admin status:', adminError);
        }
      }

      // Check for pending invitations if user has no organization (skip for guest users)
      let hasPendingInvitation = false;
      let pendingInvitationEmail = '';
      if (!isGuest && !hasOrganization && firebaseUser.email) {
        try {
          const invitationFound = await withStartupSpan(
            'startup.auth.invitation_check',
            'Check pending invitations',
            async () => {
              const invitationSnapshot = await firestoreDb
                .collection('userInvitations')
                .where('email', '==', firebaseUser.email)
                .where('status', '==', 'invited')
                .limit(1)
                .get();
              return !invitationSnapshot.empty;
            },
          );
          if (invitationFound) {
            hasPendingInvitation = true;
            pendingInvitationEmail = firebaseUser.email;
          }
        } catch (invitationError) {
          // eslint-disable-next-line no-console
          console.warn('[AuthStore] Error checking pending invitations:', invitationError);
        }
      }

      runInAction(() => {
        if (!this.user) return;

        // IMPORTANT: Replace user object so dependent stores (SessionsStore) re-run
        // their reactions when org membership is resolved.
        this.user = {
          ...this.user,
          hasOrganization,
          organizationId,
          organizationRole,
          isCQAdmin,
          hasPendingInvitation,
          pendingInvitationEmail,
        };
      });
    }
  }

  authStateChanged = async () => {
    this.userLoading = false;
    const isInitialBootstrap = !this.hasBootstrappedAuth;
    if (isInitialBootstrap) {
      enableStartupTracing();
    }
    let updateUserSucceeded = false;
    try {
      await this.updateUser();
      updateUserSucceeded = true;
      this.checkPendingClaimToken();
      if (this.user?.isGuest) {
        this.startGuestExpirationCheck();
      } else {
        this.stopGuestExpirationCheck();
      }
    } finally {
      if (isInitialBootstrap) {
        this.hasBootstrappedAuth = true;
        // Signed-out users, or bootstrap errors, have no HomeView redirect — stop tracing here.
        if (!updateUserSucceeded || !this.user) {
          finishStartupTracing();
        }
      }
    }
  };

  /**
   * Checks for a pending claim token in localStorage and redirects to claim route if found
   * Only redirects if user doesn't already have an organization
   */
  checkPendingClaimToken() {
    const pendingToken = localStorage.getItem('pendingClaimToken');
    if (pendingToken && this.user) {
      // Don't redirect if user already claimed or was approved — stale tokens cause 404 noise
      if (this.user.hasOrganization || this.user.canUseApp) {
        localStorage.removeItem('pendingClaimToken');
        return;
      }
      // Redirect to claim route
      this.rootStore.routerStore.goTo(RouteNames.CLAIM_INVITATION, {
        params: { token: pendingToken },
      });
    }
  }

  loginWithGoogle = async () => {
    const googleProvider = new firebase.auth.GoogleAuthProvider();
    await auth.signInWithPopup(googleProvider);
    // checkPendingClaimToken will be called by authStateChanged
    // If there's a pending claim token, it will redirect to claim route
    // Otherwise, use the normal redirect
    if (!localStorage.getItem('pendingClaimToken')) {
      this.rootStore.routerStore.goToState(this.signInRedirect);
    }
  };

  loginWithMicrosoft = async () => {
    const microsoftProvider = new firebase.auth.OAuthProvider('microsoft.com');
    await auth.signInWithPopup(microsoftProvider);
    // checkPendingClaimToken will be called by authStateChanged
    // If there's a pending claim token, it will redirect to claim route
    // Otherwise, use the normal redirect
    if (!localStorage.getItem('pendingClaimToken')) {
      this.rootStore.routerStore.goToState(this.signInRedirect);
    }
  };

  sendEmailLink = async (email: string): Promise<{ success: boolean; emailSent: boolean }> => {
    // Normalize email using shared validation utility
    // This ensures consistency when user clicks link on different device
    const normalizedEmail = normalizeEmail(email);

    // Client-side validation to reduce unnecessary function invocations
    if (!normalizedEmail) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Email is required.',
      });
      return { success: false, emailSent: false };
    }

    // Validate email format using shared validation
    if (!isValidEmail(normalizedEmail)) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Invalid email address. Please check and try again.',
      });
      return { success: false, emailSent: false };
    }

    try {
      // Call Cloud Function which verifies user exists and sends email via Resend
      const sendPasswordlessLoginLinkFn = fn.httpsCallable('sendPasswordlessLoginLink');
      const result = await sendPasswordlessLoginLinkFn({ email: normalizedEmail });

      const responseData = result.data as {
        message?: string;
        emailSent?: boolean;
        success?: boolean;
        rateLimited?: boolean;
      };

      // Store normalized email for verification when user clicks the link
      localStorage.setItem('emailForSignIn', normalizedEmail);

      // Check if email was actually sent
      // If emailSent is undefined (old function version), assume true for backwards compatibility
      // If emailSent is explicitly false, email was not sent (user doesn't exist, disabled, etc.)
      // If emailSent is true, email was successfully sent
      const emailSent =
        responseData.emailSent === undefined ? true : responseData.emailSent === true;

      // Show appropriate message based on the response
      let message: string;
      if (responseData.rateLimited) {
        // Show rate limit message - safe to show since it doesn't reveal user existence
        message = responseData.message || 'Too many requests. Please try again later.';
      } else if (emailSent) {
        // Email was successfully sent
        message = 'Sign-in link sent! Check your email inbox.';
      } else {
        // Email was not sent (user doesn't exist, disabled, etc.) - use generic message to prevent user enumeration
        message =
          responseData.message ||
          'If an account exists with this email, a sign-in link has been sent.';
      }

      // Always show snackbar notification for user feedback
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: message,
      });

      // Log for debugging
      // eslint-disable-next-line no-console
      console.log('[sendEmailLink] Response:', { emailSent, message, responseData });

      return { success: true, emailSent };
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('Error sending passwordless login link:', error);

      // Handle specific error cases
      // Note: The server never throws 'functions/not-found' to prevent user enumeration
      // It always returns a generic success message regardless of user existence
      let errorMessage = 'Failed to send sign-in link. Please try again.';
      if (error && typeof error === 'object' && 'code' in error) {
        const errorCode = error.code as string;
        // Handle specific error codes first (order matters - most specific first)
        // Support both 'functions/xxx' and 'xxx' formats for backwards compatibility
        if (errorCode === 'functions/invalid-argument' || errorCode === 'invalid-argument') {
          errorMessage = 'Invalid email address. Please check and try again.';
        } else if (
          errorCode === 'functions/failed-precondition' ||
          errorCode === 'failed-precondition'
        ) {
          errorMessage = 'Unable to send email. Please try again later.';
        } else if (errorCode === 'functions/internal' || errorCode === 'internal') {
          errorMessage = 'Failed to send email. Please try again later.';
        } else if ('message' in error && typeof error.message === 'string') {
          // Use error message as fallback only if no specific code matched
          errorMessage = error.message;
        }
      }

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      // Return failure - caller can use this to show error state
      return { success: false, emailSent: false };
    }
  };

  verifyEmailLink = async () => {
    if (auth.isSignInWithEmailLink(window.location.href)) {
      let email = localStorage.getItem('emailForSignIn');

      let promptUnavailable = false;
      if (!email) {
        // User opened the link on a different device - ask for email if prompt is available.
        // In Electron (e.g. Cursor) and some embedded contexts, window.prompt is not supported.
        if (typeof window.prompt === 'function') {
          try {
            // eslint-disable-next-line no-alert
            email = window.prompt('Please provide your email for confirmation');
          } catch {
            email = null;
            promptUnavailable = true;
          }
        } else {
          promptUnavailable = true;
        }
        if (promptUnavailable) {
          this.rootStore.uiSnackbarStore.setSnackbarDetails({
            description:
              'Open this link in the same browser where you requested sign-in, or use a browser that supports entering your email.',
          });
        }
      }

      if (email) {
        try {
          await auth.signInWithEmailLink(email, window.location.href);
          localStorage.removeItem('emailForSignIn');
          // Clear the URL parameters after successful sign-in
          window.history.replaceState({}, document.title, window.location.pathname);
          this.rootStore.routerStore.goToState(this.signInRedirect);
        } catch (error: unknown) {
          // eslint-disable-next-line no-console
          console.error('Error verifying email link:', error);

          let errorMessage = 'Error verifying email link. Please try again.';
          if (error && typeof error === 'object' && 'code' in error) {
            const errorCode = error.code as string;
            if (
              errorCode === 'auth/invalid-action-code' ||
              errorCode === 'auth/expired-action-code'
            ) {
              errorMessage =
                'This sign-in link has expired or is invalid. Please request a new one.';
            } else if (errorCode === 'auth/invalid-email') {
              errorMessage = 'Invalid email address. Please check and try again.';
            } else if (errorCode === 'auth/user-disabled') {
              errorMessage = 'This account has been disabled. Please contact support.';
            }
          }

          this.rootStore.uiSnackbarStore.setSnackbarDetails({
            description: errorMessage,
          });
        }
      } else if (!promptUnavailable) {
        // User cancelled the prompt (prompt was available but returned null/empty)
        this.rootStore.uiSnackbarStore.setSnackbarDetails({
          description: 'Email is required to complete sign-in. Please try again.',
        });
      }
    }
  };

  logout = async () => {
    // Dispose stores that have reactions before logging out
    this.rootStore.dispose();
    await auth.signOut();
    // Explicitly redirect to login screen (HOME route) after logout
    // This provides immediate navigation feedback. The App.tsx useEffect will also
    // trigger on auth state change, but since we're already navigating to HOME,
    // it won't cause double navigation issues.
    this.rootStore.routerStore.goTo(RouteNames.HOME);
  };

  setSignInRedirect = (toRoute: RouterState) => {
    this.signInRedirect = toRoute;
  };
}
